import React, { useState } from 'react';
import { Button } from './ui/button';
import { Shield, FileText, ExternalLink, Loader2 } from 'lucide-react';
import api from '../lib/api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function GdprConsentModal() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Only show if user is logged in and has no gdpr_consent_date
  if (!user || (user as Record<string, unknown>).gdpr_consent_date) return null;

  const handleAccept = async () => {
    setLoading(true);
    try {
      await api.post('/auth/gdpr-consent', { accept: true });
      await refreshUser();
      toast.success('Thank you! Your consent has been recorded.');
    } catch {
      toast.error('Failed to record consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gdpr-consent-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center">
            <Shield size={24} className="text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-neutral-900">Privacy Update</h2>
            <p className="text-xs text-muted-foreground">Your data, your rights</p>
          </div>
        </div>

        <div className="bg-neutral-50 rounded-2xl p-4 mb-4 space-y-3">
          <p className="text-sm text-neutral-700 leading-relaxed">
            We've updated our policies to comply with GDPR regulations. To continue using DutchDate, please review and accept our updated terms:
          </p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2 text-sm text-neutral-600">
              <FileText size={14} className="text-teal-500 mt-0.5 shrink-0" />
              <span>We process your data only for matching, safety, and service improvement</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-600">
              <FileText size={14} className="text-teal-500 mt-0.5 shrink-0" />
              <span>You can export or delete your data anytime from Settings</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-600">
              <FileText size={14} className="text-teal-500 mt-0.5 shrink-0" />
              <span>Inactive accounts are automatically purged after 12 months</span>
            </li>
          </ul>
        </div>

        <div className="flex gap-2 mb-4 text-xs">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline">
            Privacy Policy <ExternalLink size={10} />
          </a>
          <span className="text-neutral-300">|</span>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-teal-600 hover:underline">
            Terms of Service <ExternalLink size={10} />
          </a>
        </div>

        <Button
          data-testid="gdpr-accept-btn"
          onClick={handleAccept}
          disabled={loading}
          className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-heading font-semibold"
        >
          {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
          I Accept — Continue to DutchDate
        </Button>
        <p className="text-[10px] text-center text-muted-foreground mt-3">
          By clicking accept, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
